import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { MeasuredValue, Money } from '@rematter/ui-web';

import {
  DeleteButtonCell,
  EstimatedPriceFieldCell,
  InventoryTagsFieldCell,
  ItemTypeFieldCell,
  MaterialCell,
  PricingUnitFieldCell,
  RequestedQuantityFieldCell,
  UnitPriceFieldCell,
} from './fields';
import { LineItemErrors } from './types';

import { useLoadLineItemsTotals } from '~/modules/trade/hooks/useLoadLineItemsTotals';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormLineItem, WeightUnitType } from '~/modules/trade/types/load';
import {
  FooterCell,
  HeaderCell,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
} from '~/shared/components/line-items';

const GRID_TEMPLATE =
  'grid-cols-[minmax(16rem,2fr)_1fr_80px_1fr_100px_2fr_1fr_var(--size-input)]';

const GRID_TEMPLATE_NO_ACTIONS =
  'grid-cols-[minmax(16rem,2fr)_1fr_80px_1fr_100px_2fr_1fr]';

export interface StandaloneLoadTableProps {
  lineItems: LoadFormLineItem[];
  onUpdateLineItem: (id: string, updates: Partial<LoadFormLineItem>) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem?: () => void;
  disabled?: boolean;
  hideAddButton?: boolean;
  hideActions?: boolean;
  isLoading?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  errors?: Record<string, LineItemErrors>;
}

export const StandaloneLoadTable: React.FC<StandaloneLoadTableProps> = ({
  lineItems,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
  disabled = false,
  hideAddButton = false,
  hideActions = false,
  isLoading = false,
  weightUnit = 'price',
  facilityDefaultUnit,
  errors,
}) => {
  const t = useTranslations();

  const totals = useLoadLineItemsTotals({
    lineItems,
    facilityDefaultUnit: facilityDefaultUnit ?? enumUnit.Pounds,
    weightUnit,
  });

  const isEmpty = lineItems.length === 0;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={hideAddButton}
      disabled={disabled}
      isLoading={isLoading}
      addButtonLabel={t.itemTable.actions.addMaterial}
      className="@StandaloneLoadTable"
    >
      <LineItemHeader
        gridTemplate={hideActions ? GRID_TEMPLATE_NO_ACTIONS : GRID_TEMPLATE}
      >
        <HeaderCell className="justify-start">
          {t.loadsView.form.standaloneLoadHeader.materials}
        </HeaderCell>
        <HeaderCell>
          {t.loadsView.form.standaloneLoadHeader.unitPrice}
        </HeaderCell>
        <HeaderCell>
          {t.loadsView.form.standaloneLoadHeader.pricingUnit}
        </HeaderCell>
        <HeaderCell>
          {t.loadsView.form.standaloneLoadHeader.requestedWeight}
        </HeaderCell>
        <HeaderCell>{t.loadsView.form.standaloneLoadHeader.type}</HeaderCell>
        <HeaderCell>
          {t.loadsView.form.standaloneLoadHeader.inventoryTags}
        </HeaderCell>
        <HeaderCell>
          {t.loadsView.form.standaloneLoadHeader.estimatedPrice}
        </HeaderCell>
        {!hideActions && <HeaderCell />}
      </LineItemHeader>

      {lineItems.map((lineItem) => (
        <LineItemRow
          key={lineItem.id}
          gridTemplate={hideActions ? GRID_TEMPLATE_NO_ACTIONS : GRID_TEMPLATE}
        >
          <MaterialCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disableMaterialSelection={disabled}
            errors={errors?.[lineItem.id]}
          />
          <UnitPriceFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
            errors={errors?.[lineItem.id]}
          />
          <PricingUnitFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
          />
          <RequestedQuantityFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            showWeightIcon
          />
          <ItemTypeFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
          />
          <InventoryTagsFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
          />
          <EstimatedPriceFieldCell lineItem={lineItem} disabled />
          {!hideActions && (
            <DeleteButtonCell
              lineItem={lineItem}
              onDeleteLineItem={onDeleteLineItem}
              disabled={disabled}
              confirmationType="standaloneLoad"
            />
          )}
        </LineItemRow>
      ))}

      {!isEmpty && (
        <LineItemFooter
          gridTemplate={hideActions ? GRID_TEMPLATE_NO_ACTIONS : GRID_TEMPLATE}
        >
          <FooterCell className="font-bold text-neutral">
            {totals.itemsCount}{' '}
            {t.itemTable.standaloneLoadFooter.material(totals.itemsCount)}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell className="flex-col items-end gap-1 font-bold">
            {!!totals.requestedWeight && (
              <div>
                <MeasuredValue
                  value={totals.requestedWeight.value}
                  unit={totals.requestedWeight.unit}
                />
              </div>
            )}
            {!!totals.requestedEachCount && (
              <div>
                <MeasuredValue
                  value={totals.requestedEachCount}
                  unit={enumUnit.Each}
                />
              </div>
            )}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell className="justify-between font-bold">
            <Money amount={totals.totalEstimatedPrice} />
          </FooterCell>
          {!hideActions && <FooterCell />}
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
